import { createSlice } from "@reduxjs/toolkit";
import { getAccessPoint, getAllAccessPoints, getAccessPointGeneral, updateAccessPointGeneral, getAccessPointConfiguration, updateAccessPointConfiguration, updateAccessPoint, createAccessPointConfig, getAccessPointsDropdown, getAccessPointsStatistics, } from "../thunk/access-point-thunk";
/**
 * Merge SignalR payloads into an existing list row.
 * `updateAccessPointInList` is only used for AccessPointConnected / Disconnected. Those DTOs
 * are built with AutoMapper ignoring maintenance + node aggregates, so JSON carries false/0
 * and would strip maintenance controls and counts from the dashboard if applied blindly.
 */
function mergeAccessPointRow(existing, incoming) {
    var _a, _b;
    const merged = Object.assign(Object.assign({}, existing), incoming);
    if (!((_a = merged.id) === null || _a === void 0 ? void 0 : _a.trim()))
        merged.id = existing.id;
    if (!((_b = merged.accessPointUID) === null || _b === void 0 ? void 0 : _b.trim()))
        merged.accessPointUID = existing.accessPointUID;
    merged.hasMaintenanceRequest = existing.hasMaintenanceRequest;
    merged.maintenanceRequestCount = existing.maintenanceRequestCount;
    merged.nodeCount = existing.nodeCount;
    return merged;
}
const initialState = {
    accessPoints: null,
    accessPointsDropdown: null,
    dropdownLoading: false,
    dropdownError: null,
    paginationInfo: null,
    selectedAccessPoint: null,
    loading: false,
    error: null,
    statistics: null,
    statisticsLoading: false,
    statisticsError: null,
    singleLoading: false,
    singleError: null,
    updateGeneralLoading: false,
    updateGeneralError: null,
    updateConfigLoading: false,
    updateConfigError: null,
    updateLoading: false,
    updateError: null,
    configLoading: false,
    configError: null,
    filters: {
        globalSearch: undefined,
    },
    currentPageNumber: 1,
    currentPageSize: 25,
    sortColumn: null,
    sortDirection: null,
};
const accessPointsSlice = createSlice({
    name: "accessPoints",
    initialState,
    reducers: {
        setSelectedAccessPoint: (state, action) => {
            state.selectedAccessPoint = action.payload;
        },
        clearError: (state) => {
            state.error = null;
            state.singleError = null;
            state.updateError = null;
            state.configError = null;
        },
        clearAccessPoints: (state) => {
            state.accessPoints = null;
            state.paginationInfo = null;
            state.selectedAccessPoint = null;
        },
        updateFilters: (state, action) => {
            state.filters = Object.assign(Object.assign({}, state.filters), action.payload);
            state.currentPageNumber = 1; // Reset to first page on filter change
        },
        setGlobalSearch: (state, action) => {
            state.filters.globalSearch = action.payload;
            state.currentPageNumber = 1;
        },
        setPagination: (state, action) => {
            state.currentPageNumber = action.payload.pageNumber;
            state.currentPageSize = action.payload.pageSize;
        },
        setPageNumber: (state, action) => {
            state.currentPageNumber = action.payload;
        },
        setPageSize: (state, action) => {
            state.currentPageSize = action.payload;
        },
        setSorting: (state, action) => {
            state.sortColumn = action.payload.sortColumn;
            state.sortDirection = action.payload.sortDirection;
        },
        resetFiltersAndPagination: (state) => {
            state.filters = { globalSearch: undefined };
            state.currentPageNumber = 1;
            state.currentPageSize = 25;
            state.sortColumn = null;
            state.sortDirection = null;
        },
        addAccessPointToList: (state, action) => {
            if (state.accessPoints) {
                // Check if AP already exists
                const existingIndex = state.accessPoints.findIndex((ap) => ap.accessPointUID === action.payload.accessPointUID);
                if (existingIndex === -1) {
                    // The API returns the list ordered by accessPointName ascending. Prepending put a
                    // live-added AP at the top regardless of its name, so the row jumped somewhere else
                    // on the next fetch; insert it where the server would have placed it instead.
                    const incomingName = action.payload.accessPointName || "";
                    const insertAt = state.accessPoints.findIndex((ap) => (ap.accessPointName || "").localeCompare(incomingName, undefined, {
                        sensitivity: "base",
                    }) > 0);
                    if (insertAt === -1) {
                        state.accessPoints.push(action.payload);
                    }
                    else {
                        state.accessPoints.splice(insertAt, 0, action.payload);
                    }
                    // Update pagination total count
                    if (state.paginationInfo) {
                        state.paginationInfo.totalCount += 1;
                    }
                }
            }
        },
        updateAccessPointInList: (state, action) => {
            var _a;
            const incoming = action.payload;
            if (!incoming.accessPointUID)
                return;
            if (state.accessPoints) {
                const index = state.accessPoints.findIndex((ap) => ap.accessPointUID === incoming.accessPointUID);
                if (index !== -1) {
                    state.accessPoints[index] = mergeAccessPointRow(state.accessPoints[index], incoming);
                }
            }
            if (((_a = state.selectedAccessPoint) === null || _a === void 0 ? void 0 : _a.accessPointUID) === incoming.accessPointUID) {
                state.selectedAccessPoint = mergeAccessPointRow(state.selectedAccessPoint, incoming);
            }
        },
        /**
         * Adjust statistics counts in-memory when a SignalR event adds or changes an AP's
         * active state. Avoids a full statistics re-fetch on every SignalR event.
         */
        updateStatisticsOnSignalR: (state, action) => {
            if (!state.statistics)
                return;
            switch (action.payload) {
                case "added":
                    state.statistics.totalCount += 1;
                    state.statistics.activeCount += 1;
                    break;
                case "activated":
                    state.statistics.activeCount += 1;
                    state.statistics.inactiveCount = Math.max(0, state.statistics.inactiveCount - 1);
                    break;
                case "deactivated":
                    state.statistics.inactiveCount += 1;
                    state.statistics.activeCount = Math.max(0, state.statistics.activeCount - 1);
                    break;
            }
        },
        /**
         * Only refresh last-activity time from temperature / AP timestamp SignalR events.
         * Do not force isActive/isConnected: that overwrote AccessPointDisconnected when a stale
         * temperature or AccessPointTimestampUpdated message arrived after the AP went offline,
         * so the UI disagreed with a full page reload (which re-fetches truth from the API).
         * Reconnection is signaled by AccessPointConnected / full list fetch, not by this patch.
         */
        patchAccessPointUpdatedAt: (state, action) => {
            var _a;
            const { accessPointUid, updatedAt, endPointCommunicationMonitoring, } = action.payload;
            const mergeRow = (row) => {
                const next = Object.assign(Object.assign({}, row), { updatedAt });
                if (endPointCommunicationMonitoring !== undefined) {
                    next.endPointCommunicationMonitoring =
                        endPointCommunicationMonitoring;
                }
                return next;
            };
            if (state.accessPoints) {
                const index = state.accessPoints.findIndex((ap) => ap.accessPointUID === accessPointUid);
                if (index !== -1) {
                    state.accessPoints[index] = mergeRow(state.accessPoints[index]);
                }
            }
            if (((_a = state.selectedAccessPoint) === null || _a === void 0 ? void 0 : _a.accessPointUID) === accessPointUid) {
                state.selectedAccessPoint = mergeRow(state.selectedAccessPoint);
            }
        },
    },
    extraReducers: (builder) => {
        builder
            // Get all access points
            .addCase(getAllAccessPoints.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(getAllAccessPoints.fulfilled, (state, action) => {
            state.loading = false;
            state.accessPoints = action.payload.items;
            state.paginationInfo = {
                totalCount: action.payload.totalCount,
                pageNumber: action.payload.pageNumber,
                pageSize: action.payload.pageSize,
                totalPages: action.payload.totalPages,
                hasPreviousPage: action.payload.hasPreviousPage,
                hasNextPage: action.payload.hasNextPage,
            };
        })
            .addCase(getAllAccessPoints.rejected, (state, action) => {
            // Aborted requests are superseded by a newer fetch — keep loading for the new one.
            if (action.meta.aborted)
                return;
            state.loading = false;
            state.error = action.payload;
        })
            // Get single access point
            .addCase(getAccessPoint.pending, (state) => {
            state.singleLoading = true;
            state.singleError = null;
        })
            .addCase(getAccessPoint.fulfilled, (state, action) => {
            state.singleLoading = false;
            state.selectedAccessPoint = action.payload;
        })
            .addCase(getAccessPoint.rejected, (state, action) => {
            state.singleLoading = false;
            state.singleError = action.payload;
        })
            // Get access point general settings
            .addCase(getAccessPointGeneral.pending, (state) => {
            state.singleLoading = true;
            state.singleError = null;
        })
            .addCase(getAccessPointGeneral.fulfilled, (state, action) => {
            state.singleLoading = false;
            state.selectedAccessPoint = action.payload;
        })
            .addCase(getAccessPointGeneral.rejected, (state, action) => {
            state.singleLoading = false;
            state.singleError = action.payload;
        })
            // Update access point general settings
            .addCase(updateAccessPointGeneral.pending, (state) => {
            state.updateGeneralLoading = true;
            state.updateGeneralError = null;
        })
            .addCase(updateAccessPointGeneral.fulfilled, (state, action) => {
            state.updateGeneralLoading = false;
            state.selectedAccessPoint = action.payload;
            // Update the access point in the list if it exists
            if (state.accessPoints) {
                const index = state.accessPoints.findIndex((ap) => ap.accessPointUID === action.payload.accessPointUID);
                if (index !== -1) {
                    state.accessPoints[index] = action.payload;
                }
            }
        })
            .addCase(updateAccessPointGeneral.rejected, (state, action) => {
            state.updateGeneralLoading = false;
            state.updateGeneralError = action.payload;
        })
            // Get access point configuration settings
            .addCase(getAccessPointConfiguration.pending, (state) => {
            state.singleLoading = true;
            state.singleError = null;
        })
            .addCase(getAccessPointConfiguration.fulfilled, (state, action) => {
            state.singleLoading = false;
            state.selectedAccessPoint = action.payload;
        })
            .addCase(getAccessPointConfiguration.rejected, (state, action) => {
            state.singleLoading = false;
            state.singleError = action.payload;
        })
            // Update access point configuration settings
            .addCase(updateAccessPointConfiguration.pending, (state) => {
            state.updateConfigLoading = true;
            state.updateConfigError = null;
        })
            .addCase(updateAccessPointConfiguration.fulfilled, (state, action) => {
            state.updateConfigLoading = false;
            state.selectedAccessPoint = action.payload;
            // Update the access point in the list if it exists
            if (state.accessPoints) {
                const index = state.accessPoints.findIndex((ap) => ap.accessPointUID === action.payload.accessPointUID);
                if (index !== -1) {
                    state.accessPoints[index] = action.payload;
                }
            }
        })
            .addCase(updateAccessPointConfiguration.rejected, (state, action) => {
            state.updateConfigLoading = false;
            state.updateConfigError = action.payload;
        })
            // Legacy update access point
            .addCase(updateAccessPoint.pending, (state) => {
            state.updateLoading = true;
            state.updateError = null;
        })
            .addCase(updateAccessPoint.fulfilled, (state, action) => {
            state.updateLoading = false;
            state.selectedAccessPoint = action.payload;
            // Update the access point in the list if it exists
            if (state.accessPoints) {
                const index = state.accessPoints.findIndex((ap) => ap.accessPointUID === action.payload.accessPointUID);
                if (index !== -1) {
                    state.accessPoints[index] = action.payload;
                }
            }
        })
            .addCase(updateAccessPoint.rejected, (state, action) => {
            state.updateLoading = false;
            state.updateError = action.payload;
        })
            // Create access point config (download)
            .addCase(createAccessPointConfig.pending, (state) => {
            state.configLoading = true;
            state.configError = null;
        })
            .addCase(createAccessPointConfig.fulfilled, (state) => {
            state.configLoading = false;
        })
            .addCase(createAccessPointConfig.rejected, (state, action) => {
            state.configLoading = false;
            state.configError = action.payload;
        })
            // Get access points dropdown
            .addCase(getAccessPointsDropdown.pending, (state) => {
            state.dropdownLoading = true;
            state.dropdownError = null;
        })
            .addCase(getAccessPointsDropdown.fulfilled, (state, action) => {
            state.dropdownLoading = false;
            state.accessPointsDropdown = action.payload;
        })
            .addCase(getAccessPointsDropdown.rejected, (state, action) => {
            state.dropdownLoading = false;
            state.dropdownError = action.payload;
            state.accessPointsDropdown = null;
        })
            // Get access points statistics
            .addCase(getAccessPointsStatistics.pending, (state) => {
            state.statisticsLoading = true;
            state.statisticsError = null;
        })
            .addCase(getAccessPointsStatistics.fulfilled, (state, action) => {
            state.statisticsLoading = false;
            state.statistics = action.payload;
        })
            .addCase(getAccessPointsStatistics.rejected, (state, action) => {
            state.statisticsLoading = false;
            state.statisticsError = action.payload;
        });
    },
});
export const { setSelectedAccessPoint, clearError, clearAccessPoints, updateFilters, setGlobalSearch, setPagination, setPageNumber, setPageSize, setSorting, addAccessPointToList, updateAccessPointInList, updateStatisticsOnSignalR, patchAccessPointUpdatedAt, resetFiltersAndPagination, } = accessPointsSlice.actions;
export default accessPointsSlice.reducer;
